// Chapter 15 - Negative Punishment
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 15.
// All content traced to source-pdfs/Chapter-15-Negative-Punishment-4.pdf

import type { ChapterModule } from "./types";

const chapter15Module: ChapterModule = {
  meta: {
    id: "15",
    number: 15,
    title: "Negative Punishment",
    subtitle: "Time-out, response cost",
    part: "Part 5 - Punishment",
    estMinutes: 65,
    bacb: ["B-6", "G-16"],
  },
  curated: true,
  overview: [
    "Chapter 15 covers the two main behavior-change tactics built on negative punishment: TIME-OUT from positive reinforcement and RESPONSE COST. Both reduce target behavior by REMOVING positive reinforcement contingent on the response.",
    "Time-out is the immediate, response-contingent withdrawal of the opportunity to earn positive reinforcers (or loss of access to existing ones) for a specified time. Response cost is a fine: response-contingent loss of a specific number of positive reinforcers. The chapter details nonexclusion vs exclusion time-out, the three exit-criteria dimensions, response-cost methods (fining an existing cache vs a bonus reserve), and the ethical and treatment-integrity considerations specific to each procedure.",
  ],
  objectives: [
    { id: "B-6",  text: "Define and provide examples of positive and negative punishment contingencies." },
    { id: "G-16", text: "Use time-out and response cost procedures appropriately and ethically." },
  ],
  coreConcepts: [
    {
      title: "Time-out from positive reinforcement: definition",
      body: [
        "Time-out is the immediate response-contingent withdrawal of the opportunity to earn positive reinforcers, or the immediate loss of access to positive reinforcers for a specified time. To FUNCTION as negative punishment, the procedure must decrease future occurrences of similar behaviors.",
      ],
      bullets: [
        "The discrepancy between 'time-in' and 'time-out' environments must be DISCRIMINATIVE.",
        "Response-contingent loss of access to reinforcement must occur IMMEDIATELY.",
        "A decrease in the future frequency of the time-out-producing behavior must actually occur.",
      ],
    },
    {
      title: "Nonexclusion time-out",
      body: [
        "Contingent on the target behavior, the participant REMAINS in the time-in setting but elements are changed to remove access to reinforcement. Four tactics: PLANNED IGNORING (withdraw attention), TERMINATE SPECIFIC REINFORCER (e.g., remove a preferred item), CONTINGENT OBSERVATION (reposition so the person can observe ongoing activity but not earn reinforcement), and PARTITION/SELECT SPACE TIME-OUT (move to a different space within the time-in setting).",
      ],
    },
    {
      title: "Exclusion time-out",
      body: [
        "Physical separation of the participant from the time-in environment when time-out goes into effect. Two tactics: (a) the participant is removed from the time-in environment, or (b) the time-in environment is removed from the participant.",
      ],
    },
    {
      title: "Why time-out is often preferred over additive aversives",
      body: [
        "Time-out is desirable because of ease of application, acceptability, rapid suppression effects, and the ability to combine it with other approaches. It can be effective without introducing physically aversive stimulation, which keeps it within the least-restrictive-alternative doctrine.",
      ],
    },
    {
      title: "Effectiveness conditions for time-out",
      body: [
        "Time-in must be REINFORCING. If time-in is boring or aversive, time-out can paradoxically reinforce the target behavior. Behaviors leading to time-out, its duration, exit criteria, and the overall program length must be explicitly stated. Practitioners decide between nonexclusion and exclusion based on intrusiveness and feasibility.",
      ],
    },
    {
      title: "Three exit-criteria dimensions",
      body: [
        "Exit criteria are conceptualized in three dimensions: (a) duration of any individual time-out session, (b) behavioral criteria for leaving time-out and returning to time-in, and (c) overall length of the time-out PROGRAM (how long the procedure remains in place as part of treatment).",
      ],
    },
    {
      title: "Response cost: definition and forms",
      body: [
        "Response cost is the response-contingent loss of a specific number of positive reinforcers that has the effect of decreasing the future occurrence of similar responses. It functions as a fine. Lost reinforcers are typically generalized conditioned reinforcers (tokens), tangibles, or minutes of already-earned time with preferred activities.",
      ],
    },
    {
      title: "Two methods for response cost",
      body: [
        "(a) Levy a fine against the participant's existing cache of previously earned reinforcers. (b) Levy a fine against the participant's surplus of extra, noncontingently provided reinforcers (BONUS response cost: extra reinforcers given specifically so they can be removed contingent on the target behavior).",
      ],
    },
    {
      title: "Why response cost is attractive",
      body: [
        "Effective, meets the standard for least restrictive alternative, produces rapid suppression, convenient, can be combined with other procedures, and is socially valid. Punitive value depends entirely on the individual reinforcement preferences of the participant.",
      ],
    },
    {
      title: "Effective use of response cost",
      body: [
        "Deliver fines IMMEDIATELY; decide whether bonus response cost is preferable; ensure a reinforcer reserve so the participant cannot go negative; recognize the potential for unplanned outcomes; avoid overusing response cost; keep records on its effects.",
      ],
    },
    {
      title: "Response cost considerations and side effects",
      body: [
        "Possible problems: increased aggression, avoidance responses, collateral reductions of desired behaviors, drawing attention to the punished behavior, unpredictable effects, and treatment-integrity challenges. Permission is required for most applications. Like all punishment, time-out and response cost should be used only after positive reductive procedures have failed and with monitoring, supervision, and evaluation in place.",
      ],
    },
  ],
  glossary: [
    { term: "Time-out from positive reinforcement", definition: "Immediate response-contingent withdrawal of the opportunity to earn positive reinforcers or loss of access to reinforcers for a specified time; functions as negative punishment when behavior decreases.", sourceHint: "Time-out from positive reinforcement (or simply time-out) is the immediate response-contingent withdrawal of the opportunity to earn positive reinforcers or the immediate loss of access to positive reinforcers for a specified time." },
    { term: "Nonexclusion time-out", definition: "Time-out tactic in which the participant remains in the time-in setting while elements of that setting are altered to remove access to reinforcement.", sourceHint: "Nonexclusion time-out means that contingent on the occurrence of the target behavior, the participant remains physically within the time-in setting, but elements of that setting are changed." },
    { term: "Exclusion time-out", definition: "Physical separation of the participant from the time-in environment when time-out goes into effect.", sourceHint: "Exclusion time-out is the physical separation of the participant from the time-in environment when time-out goes into effect." },
    { term: "Planned ignoring", definition: "Nonexclusion time-out tactic in which attention/reinforcement is withdrawn contingent on the target behavior.", sourceHint: "planned ignoring, terminate specific reinforcer contact, contingent observation, and partition/select space time-out." },
    { term: "Contingent observation", definition: "Nonexclusion time-out tactic in which the participant is repositioned to observe ongoing activity but cannot earn reinforcement.", sourceHint: "repositioning occurs so that observations of ongoing activity continue, but without reinforcement." },
    { term: "Response cost", definition: "Response-contingent loss of a specific number of positive reinforcers, decreasing future occurrence of similar responses.", sourceHint: "Response cost is the response-contingent loss of a specific number of positive reinforcers that has the effect of decreasing the future occurrence of similar responses." },
    { term: "Bonus response cost", definition: "Response cost variant in which additional reinforcers are made available noncontingently specifically for removal contingent on the target behavior.", sourceHint: "Bonus response cost operates when practitioners make additional reinforcers available noncontingently to the participant, specifically for removal when a target behavior occurs." },
    { term: "Reinforcer reserve", definition: "A buffer of available reinforcers used in response-cost programs to avoid 'going negative' when fines are levied.", sourceHint: "ensure reinforcer reserve, recognize the potential for unplanned or unexpected outcomes." },
  ],
  procedures: [
    {
      title: "Set up a time-out program",
      intro: "Explicitness and time-in quality determine effectiveness.",
      steps: [
        { title: "Confirm time-in is reinforcing", detail: "If time-in is boring or aversive, time-out cannot function as negative punishment." },
        { title: "Decide nonexclusion vs exclusion", detail: "Use least intrusive option that fits the behavior; consider safety and feasibility." },
        { title: "Define duration and exit criteria", detail: "Specify session duration, behavioral exit criteria (e.g., quiet for 30s), and overall program length." },
        { title: "Document permission and ethics", detail: "Obtain required permissions; ensure monitoring, supervision, and evaluation are in place." },
        { title: "Train implementers", detail: "Train delivery, exit criteria, and recording; check fidelity periodically." },
        { title: "Record and review", detail: "Graph time-outs and target behavior; revise if behavior does not decrease." },
      ],
    },
    {
      title: "Implement response cost",
      intro: "Treat fines like reinforcement: timed, fair, recorded.",
      steps: [
        { title: "Pick the method", detail: "Existing cache fine or bonus response cost (recommended when there is no established cache)." },
        { title: "Set fine amounts", detail: "Calibrate so the fine is meaningful but the participant still has access to reinforcers." },
        { title: "Deliver immediately", detail: "Levy fines contingent and immediate after the target behavior." },
        { title: "Maintain reserve", detail: "Avoid letting reinforcers run out, which weakens future fines and may shift the program toward extinction." },
        { title: "Record effects and watch for side effects", detail: "Track aggression, avoidance, collateral decreases of desired behavior; revise as needed." },
      ],
    },
  ],
  examples: [
    { scenario: "Toddler hits sibling at lunch; parent immediately turns away and withholds attention for 60 seconds.",
      application: "Planned ignoring - nonexclusion time-out by withdrawing attention.",
      sourceHint: "planned ignoring (nonexclusion time-out tactic)." },
    { scenario: "Student disrupts class; teacher moves the student to a quiet corner where they can observe but not participate, for 3 minutes.",
      application: "Contingent observation - nonexclusion time-out in which the participant observes but cannot earn reinforcement.",
      sourceHint: "repositioning occurs so that observations of ongoing activity continue, but without reinforcement." },
    { scenario: "Token economy: student loses 2 tokens for blurting out an answer; blurting decreases.",
      application: "Response cost via fine against existing token cache.",
      sourceHint: "The practitioner can (a) levy a fine against an individual's existing cache of previously earned reinforcers." },
    { scenario: "Therapist gives child 5 extra tokens at start of session; tokens are removed one at a time when the target behavior occurs.",
      application: "Bonus response cost.",
      sourceHint: "Bonus response cost operates when practitioners make additional reinforcers available noncontingently to the participant, specifically for removal when a target behavior occurs.",
    },
  ],
  comparisons: [
    {
      title: "Time-out vs Response cost",
      left:  { label: "Time-out", points: [
        "Loss of OPPORTUNITY to earn reinforcement for a time.",
        "Often used with younger learners or escalating behavior.",
        "Requires time-in to be reinforcing.",
      ]},
      right: { label: "Response cost", points: [
        "Loss of SPECIFIC reinforcers already earned or banked.",
        "Often used in token economies and with verbal learners.",
        "Requires reinforcer reserve to avoid going negative.",
      ]},
    },
    {
      title: "Nonexclusion vs Exclusion time-out",
      left:  { label: "Nonexclusion", points: [
        "Participant remains in time-in setting.",
        "Tactics: planned ignoring, terminate specific reinforcer, contingent observation, partition.",
        "Less intrusive; preferred when feasible.",
      ]},
      right: { label: "Exclusion", points: [
        "Participant physically separated from the time-in environment (or time-in removed from them).",
        "More intrusive; requires more safeguards.",
      ]},
    },
    {
      title: "Existing-cache fine vs Bonus response cost",
      left:  { label: "Existing cache fine", points: [
        "Fine levied against previously earned reinforcers.",
        "Risk: the participant resents losing earned tokens.",
      ]},
      right: { label: "Bonus response cost", points: [
        "Extra reinforcers added noncontingently and removed contingent on behavior.",
        "Often more socially acceptable.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "Sending a child to a quiet room is automatically time-out.",
      reality: "Time-out is defined by FUNCTION. If the 'quiet room' is more reinforcing than the time-in setting (e.g., escape from demands), the procedure may actually reinforce the behavior.",
      sourceHint: "The time-in environment must be reinforcing if time-out is to be effective.",
    },
    {
      myth: "Time-out duration should be long for serious behaviors.",
      reality: "Longer is not necessarily better. Duration must be set explicitly with behavioral exit criteria and the overall program length, all decided before launching.",
      sourceHint: "The exit criteria for time-out can be conceptualized in three dimensions: (a) the duration of any individual time-out session, (b) the behavioral criteria for leaving time-out and returning to the time-in setting, and (c) the overall length of the time-out program.",
    },
    {
      myth: "Response cost only works in tightly controlled token economies.",
      reality: "Response cost can be implemented with any system that delivers positive reinforcers, including time with preferred activities or tangibles, provided fines are immediate and there is a reserve.",
      sourceHint: "Response cost typically involves the loss of one or more generalized conditioned reinforcers, tangibles, or minutes of already-earned time with a preferred activity.",
    },
    {
      myth: "Time-out and response cost are intrinsically less harmful than positive punishment.",
      reality: "Both are negative-punishment procedures and may produce side effects including aggression, avoidance, and collateral reductions of desired behavior. Ethical and integrity safeguards still apply.",
      sourceHint: "Implementing response cost may increase aggression, produce avoidance responses, affect collateral reductions of desired behaviors, call attention to the punished behavior, be unpredictable, and suffer treatment integrity challenges.",
    },
    {
      myth: "Bonus response cost is just adding more reinforcement.",
      reality: "Bonus response cost is engineered so the noncontingent extras can be REMOVED contingent on the target behavior. The structure makes the removal more acceptable, but it is still a fine.",
      sourceHint: "Bonus response cost operates when practitioners make additional reinforcers available noncontingently to the participant, specifically for removal when a target behavior occurs.",
    },
  ],
  examChecklist: [
    "Define time-out from positive reinforcement and the three implicit factors.",
    "Distinguish nonexclusion and exclusion time-out and list the four nonexclusion tactics.",
    "Explain why time-in must be reinforcing for time-out to function.",
    "List the three exit-criteria dimensions for time-out.",
    "Define response cost and distinguish it from time-out.",
    "Distinguish the two methods of response cost (existing cache vs bonus).",
    "List five reasons response cost is attractive.",
    "List the six implementation rules for response cost.",
    "Describe at least three side effects or considerations specific to response cost.",
    "Explain why permission and ethical safeguards are required for both procedures.",
  ],
  flashcardTerms: [
    "Time-out from positive reinforcement",
    "Nonexclusion time-out", "Exclusion time-out",
    "Planned ignoring", "Contingent observation",
    "Response cost", "Bonus response cost",
    "Reinforcer reserve",
  ],
  quiz: [
    {
      id: "Q1",
      question: "Time-out functions as NEGATIVE PUNISHMENT only when:",
      options: [
        { label: "A", text: "The participant cries during the time-out" },
        { label: "B", text: "Future occurrences of similar behavior decrease" },
        { label: "C", text: "Time-out lasts at least 5 minutes" },
        { label: "D", text: "Time-out is conducted in a separate room" },
      ],
      answer: "B",
      explanation: "Like all punishment, time-out is defined by its EFFECT on future behavior frequency, not by its appearance.",
      topic: "Definition",
      sourceHint: "To function as negative punishment, the time-out procedure must decrease future occurrences of similar behaviors.",
    },
    {
      id: "Q2",
      question: "Which is a NONEXCLUSION time-out tactic?",
      options: [
        { label: "A", text: "Sending the child to a separate room" },
        { label: "B", text: "Planned ignoring" },
        { label: "C", text: "Removing the participant from the building" },
        { label: "D", text: "Verbal reprimand" },
      ],
      answer: "B",
      explanation: "Planned ignoring is a nonexclusion tactic - the participant stays in the time-in setting but loses access to reinforcement (attention).",
      topic: "Nonexclusion time-out",
      sourceHint: "Nonexclusion time-out is typically implemented using one of four tactics: planned ignoring, terminate specific reinforcer contact, contingent observation, and partition/select space time-out.",
    },
    {
      id: "Q3",
      question: "What does response cost require BEYOND simply removing reinforcement?",
      options: [
        { label: "A", text: "A specific number of reinforcers must be lost; the result is decreased future occurrence" },
        { label: "B", text: "The participant must be removed from the setting" },
        { label: "C", text: "A verbal reprimand must accompany the fine" },
        { label: "D", text: "Three observers must witness the fine" },
      ],
      answer: "A",
      explanation: "Response cost is the response-contingent loss of a SPECIFIC NUMBER of positive reinforcers that decreases future occurrences.",
      topic: "Response cost",
      sourceHint: "Response cost is the response-contingent loss of a specific number of positive reinforcers that has the effect of decreasing the future occurrence of similar responses.",
    },
    {
      id: "Q4",
      question: "Bonus response cost differs from a fine on an existing cache because:",
      options: [
        { label: "A", text: "Bonus response cost requires no recording" },
        { label: "B", text: "The reinforcers fined are added noncontingently specifically to be removed contingent on behavior" },
        { label: "C", text: "Only bonus response cost works in token economies" },
        { label: "D", text: "Bonus response cost is not a punishment procedure" },
      ],
      answer: "B",
      explanation: "Bonus response cost adds noncontingent reinforcers specifically so they can be removed when the target behavior occurs.",
      topic: "Response cost methods",
      sourceHint: "Bonus response cost operates when practitioners make additional reinforcers available noncontingently to the participant, specifically for removal when a target behavior occurs.",
    },
    {
      id: "Q5",
      question: "The three exit-criteria dimensions for time-out are:",
      options: [
        { label: "A", text: "Length of session, behavioral exit criteria, overall program length" },
        { label: "B", text: "Permission, monitoring, evaluation" },
        { label: "C", text: "Duration, intensity, schedule" },
        { label: "D", text: "Antecedent, behavior, consequence" },
      ],
      answer: "A",
      explanation: "Exit criteria are framed by length of any individual session, behavioral criteria for ending the session, and the overall length of the time-out PROGRAM.",
      topic: "Exit criteria",
      sourceHint: "The exit criteria for time-out can be conceptualized in three dimensions: (a) the duration of any individual time-out session, (b) the behavioral criteria for leaving time-out and returning to the time-in setting, and (c) the overall length of the time-out program.",
    },
    {
      id: "Q6",
      question: "If time-in is BORING for the participant, time-out is likely to:",
      options: [
        { label: "A", text: "Be highly effective" },
        { label: "B", text: "Function as escape and potentially REINFORCE the target behavior" },
        { label: "C", text: "Be considered abusive" },
        { label: "D", text: "Require longer durations" },
      ],
      answer: "B",
      explanation: "Time-out works only when time-in is reinforcing. If time-in is boring or aversive, time-out can serve as escape and reinforce the very behavior it is meant to reduce.",
      topic: "Time-in quality",
      sourceHint: "The time-in environment must be reinforcing if time-out is to be effective.",
    },
    {
      id: "Q7",
      question: "Which is NOT a possible consideration with response cost?",
      options: [
        { label: "A", text: "Increased aggression" },
        { label: "B", text: "Avoidance responses" },
        { label: "C", text: "Drawing attention to the punished behavior" },
        { label: "D", text: "Automatic generalization to all settings" },
      ],
      answer: "D",
      explanation: "Side effects include aggression, avoidance, collateral reductions, drawing attention to the behavior, unpredictability, and integrity challenges - NOT guaranteed generalization.",
      topic: "Considerations",
      sourceHint: "Implementing response cost may increase aggression, produce avoidance responses, affect collateral reductions of desired behaviors, call attention to the punished behavior, be unpredictable, and suffer treatment integrity challenges.",
    },
  ],
  reviewPrompts: [
    "Diagram a time-out procedure with the three implicit factors labeled.",
    "Pick one of the four nonexclusion time-out tactics for a real behavior and defend the choice.",
    "Design a response cost program that uses bonus response cost and explain why bonus is preferable.",
    "Identify a case where time-out would fail and explain why.",
    "Plan permission, monitoring, and supervision steps for an exclusion time-out program.",
    "Compare time-out and response cost on intrusiveness, social validity, and risk profile.",
  ],
  recap: [
    "Both time-out and response cost are negative-punishment procedures. Time-out removes ACCESS to reinforcement for a specified time; response cost removes a SPECIFIC NUMBER of reinforcers contingent on the response.",
    "Effective time-out requires a reinforcing time-in environment, explicit duration and exit criteria, and a choice between nonexclusion (planned ignoring, terminate reinforcer, contingent observation, partition) and exclusion variants. Permission and ethical safeguards are required.",
    "Response cost is attractive (effective, least restrictive, rapid, convenient, socially valid) but depends on individual reinforcement preferences and demands a reserve to avoid going negative. Use immediately, consider bonus response cost, track effects, and watch for aggression, avoidance, and collateral reductions.",
  ],
  sourceExcerpts: [
    {
      heading: "Time-out definition",
      text: "Time-out from positive reinforcement (or simply time-out) is the immediate response-contingent withdrawal of the opportunity to earn positive reinforcers or the immediate loss of access to positive reinforcers for a specified time. To function as negative punishment, the time-out procedure must decrease future occurrences of similar behaviors. Implicit in the definition of time-out are three important factors: (a) The discrepancy between the 'time-in' and the time-out environments must be discriminative, (b) the response-contingent loss of access to reinforcement must occur immediately, and (c) a resultant decrease in the future frequency of the time-out-producing behavior must occur.",
    },
    {
      heading: "Nonexclusion and exclusion tactics",
      text: "Nonexclusion time-out means that contingent on the occurrence of the target behavior, the participant remains physically within the time-in setting. Nonexclusion time-out is typically implemented using one of four tactics: planned ignoring, terminate specific reinforcer contact, contingent observation, and partition/select space time-out. Exclusion time-out is the physical separation of the participant from the time-in environment when time-out goes into effect.",
    },
    {
      heading: "Response cost definition",
      text: "Response cost is the response-contingent loss of a specific number of positive reinforcers that has the effect of decreasing the future occurrence of similar responses. Response cost constitutes a fine for misbehavior. Response cost is implemented using two principal methods. The practitioner can (a) levy a fine against an individual's existing cache of previously earned reinforcers; or (b) levy a fine against an individual's surplus of extra, noncontingently provided reinforcers.",
    },
    {
      heading: "Response cost considerations",
      text: "Implementing response cost may increase aggression, produce avoidance responses, affect collateral reductions of desired behaviors, call attention to the punished behavior, be unpredictable, and suffer treatment integrity challenges.",
    },
  ],
};

export default chapter15Module;
